import React, { useCallback, useEffect, useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Megaphone, FlaskConical } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import PostEditor from "./PostEditor";
import PostsBoard from "./PostsBoard";
import ContentCalendar from "./ContentCalendar";
import MarketingStatus from "./MarketingStatus";

export default function MarketingPanel() {
  const [tab, setTab] = useState("studio");
  const [posts, setPosts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [editing, setEditing] = useState(null);
  const [presetDate, setPresetDate] = useState("");

  const load = useCallback(async () => {
    try {
      const [p, s] = await Promise.all([api.get("/admin/marketing/posts"), api.get("/admin/marketing/summary")]);
      setPosts(p.data); setSummary(s.data);
    } catch { toast.error("Failed to load marketing data"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const upsert = (doc) => {
    setPosts((list) => (doc._deleted ? list.filter((x) => x.id !== doc.id) : list.some((x) => x.id === doc.id) ? list.map((x) => (x.id === doc.id ? doc : x)) : [doc, ...list]));
    api.get("/admin/marketing/summary").then(({ data }) => setSummary(data)).catch(() => {});
  };
  const openEditor = (p) => { setEditing(p); setPresetDate(""); setTab("studio"); };
  const newOn = (d) => { setEditing(null); setPresetDate(d); setTab("studio"); };
  const onSaved = (doc) => { upsert(doc); setEditing(null); setPresetDate(""); setTab("approvals"); };
  const openFromCalendar = (p) => (p.status === "draft" ? openEditor(p) : setTab("approvals"));
  const pending = summary?.counts?.draft || 0;

  return (
    <div data-testid="panel-marketing">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-blue-800 to-sky-500 text-white"><Megaphone className="h-5 w-5" /></div>
          <div><h2 className="text-2xl font-bold text-slate-900">AI Marketing</h2><p className="text-sm text-slate-500">Create, approve and schedule PTG posts for Instagram & Facebook</p></div>
        </div>
        {summary?.test_mode && (
          <div className="flex items-center gap-2 rounded-xl bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800 ring-1 ring-amber-200" data-testid="mk-test-mode-banner">
            <FlaskConical className="h-4 w-4" /> TEST MODE — posts are not really sent to Instagram/Facebook yet
          </div>
        )}
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-5 flex-wrap h-auto">
          <TabsTrigger value="studio" data-testid="mk-tab-studio">Content Studio</TabsTrigger>
          <TabsTrigger value="approvals" data-testid="mk-tab-approvals">Approvals {pending > 0 && <span className="ml-1.5 rounded-full bg-amber-500 px-1.5 text-[10px] font-bold text-white">{pending}</span>}</TabsTrigger>
          <TabsTrigger value="calendar" data-testid="mk-tab-calendar">Calendar</TabsTrigger>
          <TabsTrigger value="status" data-testid="mk-tab-status">Connections & Sources</TabsTrigger>
        </TabsList>
        <TabsContent value="studio"><PostEditor post={editing} presetDate={presetDate} onSaved={onSaved} /></TabsContent>
        <TabsContent value="approvals"><PostsBoard posts={posts} onChange={upsert} onEdit={openEditor} liveOff={!!summary?.test_mode} /></TabsContent>
        <TabsContent value="calendar"><ContentCalendar posts={posts} onOpen={openFromCalendar} onNew={newOn} /></TabsContent>
        <TabsContent value="status"><MarketingStatus summary={summary} onRefresh={load} /></TabsContent>
      </Tabs>
    </div>
  );
}
